import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import { useAuth } from "../context/authContext";
import {
  getSriLankaToday,
  getSriLankaDaysAgo,
  formatSriLankaDateTime,
} from "../utils/dateUtils";
import { encryptFilters } from "../utils/encryptUtils";
import FloatingNavigation from "../components/FloatingNavigation";
import {
  FaDownload,
  FaShare,
  FaSync,
  FaFilter,
  FaEye,
  FaDollarSign,
  FaTicketAlt,
  FaMapMarkerAlt,
  FaChartLine,
  FaTimes,
  FaCopy,
  FaTable,
  FaUsers,
  FaInfoCircle,
} from "react-icons/fa";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

function AdminSalesOverview() {
  const { token } = useAuth();
  const navigate = useNavigate();

  // Helper function to format date and time
  const formatDateTime = (sale) => {
    if (!sale.date || !sale.time) return "Invalid Date";
    try {
      // Handle both date formats: YYYY-MM-DD and ISO string
      let dateTimeString;
      if (sale.date.includes("T")) {
        // Already ISO format
        dateTimeString = sale.date;
      } else {
        // Combine date and time
        dateTimeString = `${sale.date}T${sale.time}`;
      }

      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) {
        return "Invalid Date";
      }

      return formatSriLankaDateTime(dateTimeString);
    } catch (error) {
      console.error("Date formatting error:", error, sale);
      return "Invalid Date";
    }
  };

  // State management
  const [sales, setSales] = useState([]);
  const [sites, setSites] = useState([]);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [viewMode, setViewMode] = useState("table");

  // Guide modal states
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [loadingGuide, setLoadingGuide] = useState(false);

  // Filter states
  const [selectedSite, setSelectedSite] = useState("all");
  const [dateFilter, setDateFilter] = useState("Today");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [advanceFilterEnabled, setAdvanceFilterEnabled] = useState(false);
  const [agentName, setAgentName] = useState("");
  const [customerType, setCustomerType] = useState("");
  const [bookingType, setBookingType] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Share functionality
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareLink, setShareLink] = useState("");

  // Statistics
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [totalTickets, setTotalTickets] = useState(0);

  // Fetch guide details
  const fetchGuideDetails = useCallback(async (guideId) => {
    console.log("fetchGuideDetails called with guideId:", guideId);
    if (!guideId) return;
    
    setLoadingGuide(true);
    try {
      console.log("Fetching guide details from API...");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-guide-details/${guideId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      console.log("Guide details response:", response.data);
      setSelectedGuide(response.data);
      setShowGuideModal(true);
    } catch (error) {
      console.error("Error fetching guide details:", error);
      alert("Failed to fetch guide details");
    } finally {
      setLoadingGuide(false);
    }
  }, [token]);

  // Handle guide info click
  const handleGuideInfoClick = (sale) => {
    console.log("Guide info clicked for sale:", sale);
    console.log("Booking type:", sale.bookingType);
    console.log("Guide Details object:", sale.guideDetails);
    
    if (sale.bookingType === "Brought by Guide") {
      // Check if guideDetails exists and has guide information
      if (sale.guideDetails && typeof sale.guideDetails === 'object') {
        // Try to get guide ID from guideDetails object
        const guideId = sale.guideDetails.guideId || sale.guideDetails._id;
        
        console.log("Found guide ID in guideDetails:", guideId);
        
        if (guideId) {
          fetchGuideDetails(guideId);
        } else {
          console.error("No guide ID found in guideDetails object");
          console.log("Available guideDetails fields:", Object.keys(sale.guideDetails));
          alert("Guide details exist but no guide ID found. Please check the data structure.");
        }
      } else {
        console.error("No guideDetails object found");
        alert("No guide details found for this booking.");
      }
    }
  };

  // Navigate to guide sales detail page
  const handleViewGuideDetails = () => {
    if (selectedGuide) {
      navigate(`/guide-sales/${selectedGuide.guideId}`);
      setShowGuideModal(false);
    }
  };

  // Fetch sites for admin
  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/sites/active`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSites(response.data.sites || []);
    } catch (error) {
      console.error("Error fetching sites:", error);
      // Set some default sites if fetch fails
      setSites([]);
    }
  }, [token]);

  // Fetch agents
  const fetchAgents = useCallback(async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/get-users`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("Agents data received:", response.data);
      setAgents(response.data);
    } catch (error) {
      console.error("Error fetching agents:", error);
    }
  }, [token]);

  // Fetch sales data
  const fetchSales = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/get-sales?`;
      const today = getSriLankaToday();

      // Date filtering
      if (dateFilter === "Today") {
        url += `date=${today}`;
      } else if (dateFilter === "Last 7 Days") {
        const last7 = getSriLankaDaysAgo(6);
        url += `startDate=${last7}&endDate=${today}`;
      } else if (dateFilter === "Last 30 Days") {
        const last30 = getSriLankaDaysAgo(29);
        url += `startDate=${last30}&endDate=${today}`;
      } else if (dateFilter === "Custom Date" && customStartDate) {
        url += `date=${customStartDate}`;
      } else if (
        dateFilter === "Custom Range" &&
        customStartDate &&
        customEndDate
      ) {
        url += `startDate=${customStartDate}&endDate=${customEndDate}`;
      }

      // Advanced filters
      if (advanceFilterEnabled) {
        if (agentName) url += `&agentName=${encodeURIComponent(agentName)}`;
        if (customerType)
          url += `&customerType=${encodeURIComponent(customerType)}`;
        if (bookingType)
          url += `&bookingType=${encodeURIComponent(bookingType)}`;
        if (paymentMethod)
          url += `&paymentMethod=${encodeURIComponent(paymentMethod)}`;
      }

      // Site filtering for admin
      if (selectedSite !== "all") {
        url += `&site=${encodeURIComponent(selectedSite)}`;
      }

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("Sales data received:", response.data);
      setSales(response.data);

      // Debug: Check if any sales have guide data
      const guideSales = response.data.filter(sale => sale.bookingType === "Brought by Guide");
      console.log("Sales with 'Brought by Guide' booking type:", guideSales);
      
      // Debug: Log all available fields for guide sales
      if (guideSales.length > 0) {
        console.log("Sample guide sale object keys:", Object.keys(guideSales[0]));
        console.log("Full guide sale object:", guideSales[0]);
      }

      // Calculate statistics
      const revenue = response.data.reduce(
        (sum, sale) => sum + parseFloat(sale.totalPrice || 0),
        0
      );
      const tickets = response.data.reduce(
        (sum, sale) => sum + parseInt(sale.numberOfTickets || 0),
        0
      );

      setTotalRevenue(revenue);
      setTotalTickets(tickets);
    } catch (error) {
      console.error("Error fetching sales:", error);
    } finally {
      setLoading(false);
    }
  }, [
    token,
    dateFilter,
    customStartDate,
    customEndDate,
    advanceFilterEnabled,
    agentName,
    customerType,
    bookingType,
    paymentMethod,
    selectedSite,
  ]);

  // Initialize data
  useEffect(() => {
    fetchSites();
    fetchAgents();
  }, [fetchSites, fetchAgents]);

  useEffect(() => {
    fetchSales();
  }, [fetchSales]);

  // Share functionality - similar to ViewSale
  const handleShare = () => {
    const filters = {
      selectedSite,
      dateFilter,
      customStartDate,
      customEndDate,
      advanceFilterEnabled,
      agentName,
      customerType,
      bookingType,
      paymentMethod,
    };

    const encrypted = encryptFilters(filters);
    const url = `${
      window.location.origin
    }/admin-sales-share/${encodeURIComponent(encrypted)}`;
    setShareLink(url);
    setShowShareModal(true);
  };

  // Export functions
  const exportToExcel = () => {
    setExporting(true);
    
    // Helper function to get guide ID from guideDetails object
    const getGuideId = (sale) => {
      if (sale.bookingType !== "Brought by Guide") return "";
      
      // Check if guideDetails exists and extract guide ID
      if (sale.guideDetails && typeof sale.guideDetails === 'object') {
        return sale.guideDetails.guideId || sale.guideDetails._id || "N/A";
      }
      return "N/A";
    };
    
    const worksheet = XLSX.utils.json_to_sheet(
      sales.map((sale) => ({
        Date: formatDateTime(sale),
        Agent: sale.salesAgent || "N/A",
        Customer: sale.customerType || "N/A",
        Type: sale.customerType || "N/A",
        Booking: sale.bookingType || "N/A",
        GuideID: getGuideId(sale),
        Tickets: sale.numberOfTickets || 0,
        Price: `Rs. ${parseFloat(sale.totalPrice || 0).toLocaleString()}`,
        Payment: sale.paymentMethod || "N/A",
        Site: sale.siteName || "N/A",
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Overview");
    XLSX.writeFile(workbook, `Admin_Sales_Overview_${getSriLankaToday()}.xlsx`);
    setExporting(false);
  };

  // Chart data preparation
  const getSiteData = () => {
    const siteStats = sales.reduce((acc, sale) => {
      const site = sale.siteName || "Main";
      if (!acc[site]) {
        acc[site] = { name: site, sales: 0, revenue: 0 };
      }
      acc[site].sales += 1;
      acc[site].revenue += parseFloat(sale.totalPrice || 0);
      return acc;
    }, {});

    return Object.values(siteStats);
  };

  const getAgentData = () => {
    const agentStats = sales.reduce((acc, sale) => {
      const agent = sale.salesAgent || "Unknown";
      if (!acc[agent]) {
        acc[agent] = { name: agent, sales: 0, revenue: 0 };
      }
      acc[agent].sales += 1;
      acc[agent].revenue += parseFloat(sale.totalPrice || 0);
      return acc;
    }, {});

    return Object.values(agentStats).slice(0, 10); // Top 10 agents
  };

  const CHART_COLORS = [
    "#3b82f6", // Blue-500
    "#06b6d4", // Cyan-500  
    "#10b981", // Emerald-500
    "#f59e0b", // Amber-500
    "#ef4444", // Red-500
    "#8b5cf6", // Violet-500
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <FaSync className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading sales data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-4 sm:py-6 gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 flex items-center">
                <FaChartLine className="mr-3 text-blue-600" />
                Admin Sales Overview
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Comprehensive sales analytics and data management
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <button
                onClick={fetchSales}
                disabled={loading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                <FaSync className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                onClick={exportToExcel}
                disabled={exporting}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                <FaDownload className={`mr-2 h-4 w-4 ${exporting ? 'animate-spin' : ''}`} />
                Export Excel
              </button>
              <button
                onClick={handleShare}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FaShare className="mr-2 h-4 w-4" />
                Share
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <FaDollarSign className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">Total Revenue</h3>
                <p className="text-2xl font-bold text-gray-900">Rs. {totalRevenue.toLocaleString()}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <FaTicketAlt className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">Total Tickets</h3>
                <p className="text-2xl font-bold text-gray-900">{totalTickets}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <FaMapMarkerAlt className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="ml-4">
                <h3 className="text-sm font-medium text-gray-500">Total Sales</h3>
                <p className="text-2xl font-bold text-gray-900">{sales.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <FaFilter className="mr-2 text-blue-600" />
              Filters
            </h3>
            <button
              onClick={() => setAdvanceFilterEnabled(!advanceFilterEnabled)}
              className={`inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                advanceFilterEnabled
                  ? "bg-blue-100 text-blue-700 border border-blue-200"
                  : "bg-gray-100 text-gray-700 border border-gray-200"
              }`}
            >
              <FaFilter className="mr-2 h-3 w-3" />
              Advanced Filters
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
              <select
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="all">All Sites</option>
                {sites.map((site) => (
                  <option key={site._id} value={site.siteName}>
                    {site.displayName || site.siteName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Filter</label>
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value="Today">Today</option>
                <option value="Last 7 Days">Last 7 Days</option>
                <option value="Last 30 Days">Last 30 Days</option>
                <option value="Custom Date">Custom Date</option>
                <option value="Custom Range">Custom Range</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rows</label>
              <select
                value={rowsPerPage}
                onChange={(e) => setRowsPerPage(parseInt(e.target.value))}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value={20}>20 rows</option>
                <option value={50}>50 rows</option>
                <option value={100}>100 rows</option>
                <option value={sales.length}>All rows</option>
              </select>
            </div>

            {dateFilter === "Custom Date" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
              </div>
            )}

            {dateFilter === "Custom Range" && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                </div>
              </>
            )}
          </div>

          {advanceFilterEnabled && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Advanced Filters</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Agent</label>
                  <select
                    value={agentName}
                    onChange={(e) => setAgentName(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">All Agents</option>
                    {agents.map((agent) => (
                      <option key={agent._id} value={agent.fullName || agent.name}>
                        {agent.fullName || agent.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer Type</label>
                  <select
                    value={customerType}
                    onChange={(e) => setCustomerType(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">All Types</option>
                    <option value="Local">Local</option>
                    <option value="Foreigner">Foreigner</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Booking Type</label>
                  <select
                    value={bookingType}
                    onChange={(e) => setBookingType(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">All Bookings</option>
                    <option value="Walk-in (approach us)">Walk-in (approach us)</option>
                    <option value="Walk-in (approach by team)">Walk-in (approach by team)</option>
                    <option value="Online Booking">Online Booking</option>
                    <option value="Brought by Guide">Brought by Guide</option>
                    <option value="In-house">In-house</option>
                    <option value="Restaurant">Restaurant</option>
                    <option value="OTA">OTA</option>
                    <option value="DMC">DMC</option>
                    <option value="Other (Shops/Restaurants)">Other (Shops/Restaurants)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  >
                    <option value="">All Methods</option>
                    <option value="Cash">Cash</option>
                    <option value="Card">Card</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* View Mode Toggle */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setViewMode("table")}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "table"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FaTable className="mr-2 h-4 w-4" />
                Table View
              </button>
              <button
                onClick={() => setViewMode("analytics")}
                className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  viewMode === "analytics"
                    ? "bg-white text-blue-600 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FaChartLine className="mr-2 h-4 w-4" />
                Analytics View
              </button>
            </div>
            <div className="flex-1 text-right">
              <p className="text-sm text-gray-500">
                Showing <span className="font-medium text-gray-900">
                  {Math.min(rowsPerPage, sales.length)}
                </span> of <span className="font-medium text-gray-900">{sales.length}</span> results
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        {viewMode === "table" ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Agent
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Customer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Booking
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invoice No
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tickets
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Site
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sales.slice(0, rowsPerPage === sales.length ? sales.length : rowsPerPage).map((sale) => (
                    <tr key={sale._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateTime(sale)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {sale.salesAgent || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {sale.customerType || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            sale.customerType?.toLowerCase() === "local"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {sale.customerType}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                            {sale.bookingType}
                          </span>
                          {sale.bookingType === "Brought by Guide" && (
                            <button
                              onClick={() => handleGuideInfoClick(sale)}
                              className="ml-2 p-1 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors duration-200"
                              title="View Guide Details"
                              disabled={loadingGuide}
                            >
                              <FaInfoCircle className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {sale.invoiceNumber || "N/A"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {sale.numberOfTickets || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        Rs. {parseFloat(sale.totalPrice || 0).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            sale.paymentMethod?.toLowerCase() === "cash"
                              ? "bg-yellow-100 text-yellow-800"
                              : "bg-purple-100 text-purple-800"
                          }`}
                        >
                          {sale.paymentMethod || "N/A"}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-800">
                          {sale.siteName || "Main"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {sales.length === 0 && (
              <div className="text-center py-12">
                <FaEye className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No sales data</h3>
                <p className="mt-1 text-sm text-gray-500">
                  No sales data found for the selected criteria.
                </p>
              </div>
            )}

            {sales.length > rowsPerPage && (
              <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Displaying <span className="font-medium">{Math.min(rowsPerPage, sales.length)}</span> of{' '}
                    <span className="font-medium">{sales.length}</span> results
                  </div>
                  <div className="text-sm text-gray-500">
                    Change "Rows" filter above to view more results
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FaChartLine className="mr-2 text-blue-600" />
                  Sales by Site
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getSiteData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="name" stroke="#6b7280" />
                    <YAxis stroke="#6b7280" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                      }} 
                    />
                    <Bar dataKey="sales" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FaDollarSign className="mr-2 text-green-600" />
                  Revenue by Site
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={getSiteData()}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="revenue"
                      label={({ name, value }) =>
                        `${name}: Rs.${value.toLocaleString()}`
                      }
                    >
                      {getSiteData().map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                      }} 
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FaUsers className="mr-2 text-purple-600" />
                Top Performing Agents
              </h3>
              <ResponsiveContainer width="100%" height={350}>
                <BarChart data={getAgentData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#fff', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
                    }} 
                  />
                  <Bar dataKey="revenue" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Share Modal */}
        {showShareModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between pb-3">
                <h3 className="text-lg font-semibold text-gray-900">Share Sales Overview</h3>
                <button
                  onClick={() => setShowShareModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes className="h-5 w-5" />
                </button>
              </div>
              <div className="mt-2">
                <div className="flex">
                  <input
                    type="text"
                    value={shareLink}
                    readOnly
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(shareLink);
                      alert("Link copied to clipboard!");
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-r-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  >
                    <FaCopy className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center pt-3">
                <button
                  onClick={() => setShowShareModal(false)}
                  className="px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Guide Details Modal */}
        {showGuideModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border max-w-2xl shadow-lg rounded-md bg-white">
              <div className="flex items-center justify-between pb-3">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <FaUsers className="mr-2 text-blue-500" />
                  Guide Details
                </h3>
                <button
                  onClick={() => setShowGuideModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FaTimes className="h-5 w-5" />
                </button>
              </div>
              
              {loadingGuide ? (
                <div className="flex justify-center items-center py-12">
                  <div className="w-8 h-8 border-3 border-slate-200 border-t-blue-500 rounded-full animate-spin"></div>
                  <span className="ml-3 text-gray-600">Loading guide details...</span>
                </div>
              ) : selectedGuide ? (
                <div className="mt-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-700 mb-2">Basic Information</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Name:</span> {selectedGuide.guideName || "N/A"}</div>
                        <div><span className="font-medium">Guide ID:</span> {selectedGuide.guideId || "N/A"}</div>
                        <div><span className="font-medium">License Number:</span> {selectedGuide.licenseNumber || "N/A"}</div>
                        <div><span className="font-medium">Phone:</span> {selectedGuide.phoneNumber || "N/A"}</div>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-700 mb-2">Contact Details</h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Email:</span> {selectedGuide.email || "N/A"}</div>
                        <div><span className="font-medium">Address:</span> {selectedGuide.address || "N/A"}</div>
                        <div><span className="font-medium">Experience:</span> {selectedGuide.experience || "N/A"} years</div>
                        <div><span className="font-medium">Languages:</span> {selectedGuide.languages?.join(", ") || "N/A"}</div>
                      </div>
                    </div>
                  </div>
                  
                  {selectedGuide.bankDetails && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-700 mb-2">Banking Information</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div><span className="font-medium">Bank:</span> {selectedGuide.bankDetails.bankName || "N/A"}</div>
                        <div><span className="font-medium">Branch:</span> {selectedGuide.bankDetails.branchName || "N/A"}</div>
                        <div><span className="font-medium">Account Number:</span> {selectedGuide.bankDetails.accountNumber || "N/A"}</div>
                        <div><span className="font-medium">Account Holder:</span> {selectedGuide.bankDetails.accountHolderName || "N/A"}</div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between pt-4 border-t">
                    <button
                      onClick={() => setShowGuideModal(false)}
                      className="px-4 py-2 bg-gray-500 text-white text-sm rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200"
                    >
                      Close
                    </button>
                    <button
                      onClick={handleViewGuideDetails}
                      className="px-6 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors duration-200 flex items-center"
                    >
                      <FaEye className="mr-2 h-4 w-4" />
                      View Full Details
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Failed to load guide details</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <FloatingNavigation />
    </div>
  );
}

export default AdminSalesOverview;
